<script setup>
import { resolveInboxThemeColor } from 'dashboard/helper/inboxColor';
import { useIsDarkTheme } from 'dashboard/composables/useIsDarkTheme';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime, isToday, isYesterday } from 'date-fns';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'unsuspendConversation',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();
const isDarkTheme = useIsDarkTheme();
const { t } = useI18n();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const myTeams = useMapGetter('teams/getMyTeams');
const currentRole = useMapGetter('getCurrentRole');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

const assigneeDisplayName = computed(() => {
  const name = String(assignee.value.name || '').trim();
  if (!name) return '';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const initial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${parts[0]} ${initial}.`;
});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  const sender = chatMetadata.value.sender || {};
  const fromStore = senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
  return {
    ...sender,
    ...fromStore,
    name: fromStore.name || sender.name || '',
    thumbnail: fromStore.thumbnail || sender.thumbnail || '',
    availability_status:
      fromStore.availability_status || sender.availability_status,
  };
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

const isFavorited = computed(() => Boolean(props.chat.favorited));

const isResolved = computed(() => props.chat.status === 'resolved');

const isSuspended = computed(() => Boolean(props.chat.service_suspended));

const isInboxNameVisible = computed(() => !activeInbox.value);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const inboxColorStyle = computed(() => {
  if (isSuspended.value) return {};
  void isDarkTheme.value;
  const color = inbox.value ? resolveInboxThemeColor(inbox.value) : null;
  if (!color) return {};
  return {
    '--inbox-accent': color,
  };
});

const hasInboxAccent = computed(() => {
  if (isSuspended.value) return false;
  void isDarkTheme.value;
  return Boolean(inbox.value && resolveInboxThemeColor(inbox.value));
});

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1
  );
});

const lastMessageAtLabel = computed(() => {
  const timestamp = Number(props.chat.timestamp || 0);
  if (!timestamp) return '';

  const date = fromUnixTime(timestamp);
  if (isToday(date)) {
    return format(date, 'HH:mm');
  }
  if (isYesterday(date)) {
    return t('CHAT_LIST.LAST_MESSAGE_YESTERDAY');
  }
  return format(date, 'dd/MM/yyyy');
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return ['text-n-slate-11'];
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const isTeamAccessRestricted = computed(() => {
  if (currentRole.value === 'administrator') return false;
  if (props.chat?.contact_only) return false;
  const teamId = props.chat?.team_id || props.chat?.meta?.team?.id;
  if (!teamId) return false;
  const isMember = (myTeams.value || []).some(
    team => team.id === Number(teamId)
  );
  if (isMember) return false;
  const assigneeId = props.chat?.meta?.assignee?.id;
  if (assigneeId && assigneeId === currentUser.value?.id) return false;
  return true;
});

const onCardClick = async e => {
  if (props.chat?.contact_only) {
    e.preventDefault();
    try {
      const ConversationApi = (await import('dashboard/api/inbox/conversation'))
        .default;
      const response = await ConversationApi.create({
        inbox_id: props.chat.inbox_id,
        contact_id: props.chat.contact_id,
      });
      const conversation = response.data;
      const displayId = conversation.id || conversation.payload?.id;
      if (displayId) {
        store.dispatch('updateConversation', conversation);
        router.push({
          path: frontendURL(
            conversationUrl({
              accountId: accountId.value,
              activeInbox: activeInbox.value || props.chat.inbox_id,
              id: displayId,
              label: props.activeLabel,
              teamId: props.teamId,
              conversationType: props.conversationType,
              foldersId: props.foldersId,
            })
          ),
        });
      }
    } catch (_error) {
      // Ignore create failures; user can retry from contacts
    }
    return;
  }

  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onUnsuspendConversation = () => {
  closeContextMenu();
  emit('unsuspendConversation', props.chat.id);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const canToggleFavorite = computed(
  () => !props.chat?.contact_only && Number(props.chat?.id)
);

const toggleFavorite = () => {
  if (!canToggleFavorite.value) return;
  store.dispatch('toggleFavorite', {
    conversationId: props.chat.id,
    favorited: isFavorited.value,
  });
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};

const onSilenceConversation = () => {
  closeContextMenu();
  store.dispatch('silenceConversation', props.chat.id);
};

const onUnsilenceConversation = () => {
  closeContextMenu();
  store.dispatch('unsilenceConversation', props.chat.id);
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-b-0 border-r-0 border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :style="inboxColorStyle"
    :class="{
      'conversation-card-inbox bg-n-alpha-3 dark:bg-n-solid-2': hasInboxAccent,
      'border-t-0': !hasInboxAccent,
      'active animate-card-select bg-n-background':
        isActiveChat && !isTeamAccessRestricted,
      'bg-n-slate-2': selected && !isTeamAccessRestricted,
      'bg-amber-100 dark:bg-amber-900/40':
        isTeamAccessRestricted && !isActiveChat,
      'bg-amber-200 dark:bg-amber-800/50':
        isTeamAccessRestricted && isActiveChat,
      'px-0': compact,
      'px-3': !compact,
      'opacity-60 saturate-0': isResolved && !isActiveChat,
      'border-l-4 border-n-ruby-8': isSuspended,
      'bg-n-ruby-3/50 dark:bg-n-ruby-3/30':
        isSuspended && !isResolved && !isActiveChat,
      'bg-n-ruby-4/70': isSuspended && !isResolved && isActiveChat,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        class="!mt-[0.75rem]"
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div
      class="px-0 py-[0.2em] border-b group-hover:border-transparent flex-1 border-n-slate-3 min-w-0 overflow-hidden"
      style="height: 70px;"
    >
      <div class="flex items-center gap-1 my-0 mx-2 min-w-0">
        <div class="flex items-center gap-1 min-w-0 flex-1">
          <h4
            class="conversation--user text-sm my-0 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap min-w-0 text-n-slate-12"
            :class="hasUnread ? 'font-semibold' : 'font-medium'"
          >
            {{ currentContact.name }}
          </h4>
          <button
            v-if="canToggleFavorite"
            type="button"
            class="flex-shrink-0 p-0.5 rounded hover:bg-n-alpha-2 transition-opacity"
            :class="
              isFavorited ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            "
            :title="
              isFavorited
                ? $t('CHAT_LIST.UNFAVORITE_CONVERSATION')
                : $t('CHAT_LIST.FAVORITE_CONVERSATION')
            "
            @click.stop="toggleFavorite"
          >
            <i
              class="text-base"
              :class="
                isFavorited
                  ? 'i-ri-star-fill text-n-amber-9'
                  : 'i-ri-star-line text-n-slate-10'
              "
            />
          </button>
        </div>
        <CardPriorityIcon
          v-if="chat.priority"
          :priority="chat.priority"
          class="flex-shrink-0 !size-3.5"
        />
        <span
          v-if="lastMessageAtLabel"
          class="text-xxs font-normal leading-3 text-n-slate-10 whitespace-nowrap flex-shrink-0"
        >
          {{ lastMessageAtLabel }}
        </span>
      </div>
      <div class="flex items-center gap-2 my-0 mx-2 min-w-0">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          :message-preview-class="messagePreviewClass"
          class="flex-1 min-w-0 leading-6 h-6 text-sm"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="leading-6 h-6 flex-1 min-w-0 text-sm"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="text-n-slate-11 text-sm my-0 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          :class="messagePreviewClass"
        >
          <fluent-icon
            size="16"
            class="-mt-0.5 align-middle inline-block text-n-slate-10"
            icon="info"
          />
          <span class="mx-0.5">
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
        <span
          v-if="hasUnread && !chat.silenced"
          class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9 flex-shrink-0"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <div
        class="flex items-center min-w-0 gap-1 mt-0"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName
          v-if="showInboxName"
          :inbox="inbox"
          class="min-w-0 truncate"
        />
        <div
          class="flex items-center gap-1.5 flex-shrink-0 ltr:ml-auto rtl:mr-auto"
        >
          <span
            v-if="showAssignee && assigneeDisplayName"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center justify-end whitespace-nowrap"
            :title="assignee.name"
          >
            <fluent-icon
              icon="person"
              size="12"
              class="text-n-slate-11 flex-shrink-0"
            />
            <span>{{ assigneeDisplayName }}</span>
          </span>
        </div>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :service-suspended="chat.service_suspended"
        :silenced="chat.silenced"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @unsuspend-conversation="onUnsuspendConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @silence-conversation="onSilenceConversation"
        @unsilence-conversation="onUnsilenceConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>

<style scoped>
.conversation-card-inbox {
  margin-top: 2px;
  margin-left: 6px;
  margin-right: 6px;
  margin-bottom: 2px;
  border-left: 3px solid var(--inbox-accent) !important;
  border-top-right-radius: 12px !important;
  border-bottom-left-radius: 12px !important;
  border-bottom-right-radius: 12px !important;
  border-top-left-radius: 12px;
  border-top: 1px solid var(--inbox-accent);
}
</style>
